import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  getPurchaseOrders, createPurchaseOrder, updatePOStatus, 
  getSuppliers, getMaterials, createSupplier, uploadPurchaseOrder,
  addPOItems, getGRNs, createGRN, receiveGRNItem, completeGRN
} from '../services/api';
import { toast } from 'sonner';
import { Plus, Search, ShoppingCart, Truck, Upload, FileText, Package, CheckCircle, MapPin } from 'lucide-react';
import PITab from '../components/procurement/PITab';

const ProcurementPage = () => {
  const [orders, setOrders] = useState([]);
  const [grns, setGRNs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('orders');
  
  // Dialog states
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [poDialogOpen, setPODialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [grnDialogOpen, setGRNDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  
  // Form states
  const [supplierName, setSupplierName] = useState('');
  const [supplierEmail, setSupplierEmail] = useState('');
  const [supplierPhone, setSupplierPhone] = useState('');
  const [poSupplierId, setPOSupplierId] = useState('');
  const [poMaterialId, setPOMaterialId] = useState('');
  const [poQuantity, setPOQuantity] = useState(10);
  
  // Upload form
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadSupplierId, setUploadSupplierId] = useState('');
  const [uploadExpectedDate, setUploadExpectedDate] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  
  // GRN form
  const [selectedPOForGRN, setSelectedPOForGRN] = useState(null);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [receiveItem, setReceiveItem] = useState(null);
  const [receiveQty, setReceiveQty] = useState(0);
  const [warehouseLocation, setWarehouseLocation] = useState({
    warehouse: 'Main',
    zone: '',
    aisle: '',
    rack: '',
    shelf: '',
    bin: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, suppliersRes, materialsRes, grnsRes] = await Promise.all([
        getPurchaseOrders({}),
        getSuppliers(),
        getMaterials(),
        getGRNs({})
      ]);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
      setMaterials(materialsRes.data);
      setGRNs(grnsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    if (!supplierName || !supplierEmail) {
      toast.error('Name and email required');
      return;
    }
    try {
      await createSupplier({ name: supplierName, contact_person: '', email: supplierEmail, phone: supplierPhone });
      toast.success('Supplier added');
      setSupplierDialogOpen(false);
      setSupplierName('');
      setSupplierEmail('');
      setSupplierPhone('');
      fetchData();
    } catch (error) {
      toast.error('Failed to add supplier');
    }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    if (!poSupplierId || !poMaterialId) {
      toast.error('Select supplier and material');
      return;
    }
    const mat = materials.find(m => m.id === poMaterialId);
    const items = [{ 
      material_id: poMaterialId, 
      material_name: mat?.name || '', 
      quantity: poQuantity, 
      unit_price: mat?.unit_price || 0, 
      total: poQuantity * (mat?.unit_price || 0) 
    }];
    try {
      await createPurchaseOrder({ supplier_id: poSupplierId, items, expected_delivery: '', notes: '' });
      toast.success('PO created');
      setPODialogOpen(false);
      setPOSupplierId('');
      setPOMaterialId('');
      setPOQuantity(10);
      fetchData();
    } catch (error) {
      toast.error('Failed to create PO');
    }
  };

  const handleUploadPO = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadSupplierId || !uploadExpectedDate) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await uploadPurchaseOrder(uploadFile, uploadSupplierId, uploadExpectedDate, uploadNotes);
      toast.success('PO file uploaded successfully');
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadSupplierId('');
      setUploadExpectedDate('');
      setUploadNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload PO');
    }
  };

  const handleStatusUpdate = async (poId, newStatus) => {
    try {
      await updatePOStatus(poId, newStatus);
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleCreateGRN = async (po) => {
    try {
      await createGRN({ po_id: po.id, notes: '' });
      toast.success('GRN created');
      setGRNDialogOpen(false);
      setSelectedPOForGRN(null);
      fetchData();
      setActiveTab('grn');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create GRN');
    }
  };

  const handleReceiveItem = async () => {
    if (!selectedGRN || !receiveItem || receiveQty <= 0) {
      toast.error('Invalid receive data');
      return;
    }
    try {
      await receiveGRNItem(selectedGRN.id, {
        material_id: receiveItem.material_id,
        quantity_received: receiveQty,
        ...warehouseLocation
      });
      toast.success('Item received successfully');
      setReceiveDialogOpen(false);
      setReceiveItem(null);
      setReceiveQty(0);
      setWarehouseLocation({ warehouse: 'Main', zone: '', aisle: '', rack: '', shelf: '', bin: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to receive item');
    }
  };

  const handleCompleteGRN = async (grnId) => {
    try {
      await completeGRN(grnId);
      toast.success('GRN completed');
      fetchData();
    } catch (error) {
      toast.error('Failed to complete GRN');
    }
  };

  const filteredOrders = orders.filter(o => 
    o.po_number.toLowerCase().includes(search.toLowerCase()) ||
    o.supplier_name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredGRNs = grns.filter(g =>
    g.grn_number.toLowerCase().includes(search.toLowerCase()) ||
    g.po_number.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-700',
      sent: 'bg-blue-100 text-blue-700',
      partial: 'bg-yellow-100 text-yellow-700',
      complete: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="procurement-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Procurement</h1>
          <p className="text-muted-foreground">Manage purchase orders, suppliers, and goods receipt</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={supplierDialogOpen} onOpenChange={setSupplierDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="add-supplier-btn">
                <Plus size={16} className="mr-1" />Add Supplier
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Supplier</DialogTitle></DialogHeader>
              <form onSubmit={handleCreateSupplier} className="space-y-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} className="mt-1.5" data-testid="supplier-name" />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={supplierEmail} onChange={(e) => setSupplierEmail(e.target.value)} className="mt-1.5" data-testid="supplier-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={supplierPhone} onChange={(e) => setSupplierPhone(e.target.value)} className="mt-1.5" />
                </div>
                <Button type="submit" className="w-full btn-accent" data-testid="save-supplier-btn">Add Supplier</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-po-btn">
                <Upload size={16} className="mr-1" />Upload PO
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Upload Purchase Order File</DialogTitle></DialogHeader>
              <form onSubmit={handleUploadPO} className="space-y-4">
                <div>
                  <Label>PO File (PDF, Excel, CSV) *</Label>
                  <Input 
                    type="file" 
                    accept=".pdf,.xlsx,.xls,.csv"
                    onChange={(e) => setUploadFile(e.target.files[0])} 
                    className="mt-1.5" 
                    data-testid="po-file-input"
                  />
                </div>
                <div>
                  <Label>Supplier *</Label>
                  <Select value={uploadSupplierId} onValueChange={setUploadSupplierId}>
                    <SelectTrigger className="mt-1.5" data-testid="upload-supplier-select">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Expected Delivery Date *</Label>
                  <Input 
                    type="date" 
                    value={uploadExpectedDate} 
                    onChange={(e) => setUploadExpectedDate(e.target.value)} 
                    className="mt-1.5"
                    data-testid="expected-delivery-date"
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Input value={uploadNotes} onChange={(e) => setUploadNotes(e.target.value)} className="mt-1.5" placeholder="Optional notes" />
                </div>
                <Button type="submit" className="w-full btn-accent" data-testid="submit-upload-btn">Upload PO</Button>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={poDialogOpen} onOpenChange={setPODialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-accent" data-testid="create-po-btn">
                <Plus size={18} className="mr-2" />Create PO
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Create Purchase Order</DialogTitle></DialogHeader>
              <form onSubmit={handleCreatePO} className="space-y-4">
                <div>
                  <Label>Supplier *</Label>
                  <Select value={poSupplierId} onValueChange={setPOSupplierId}>
                    <SelectTrigger className="mt-1.5" data-testid="po-supplier-select">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Material *</Label>
                  <Select value={poMaterialId} onValueChange={setPOMaterialId}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      {materials.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity</Label>
                  <Input type="number" value={poQuantity} onChange={(e) => setPOQuantity(parseInt(e.target.value) || 1)} className="mt-1.5" />
                </div>
                <Button type="submit" className="w-full btn-accent" data-testid="submit-po-btn">Create PO</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-blue-50 flex items-center justify-center">
                <ShoppingCart size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{orders.length}</div>
                <div className="text-sm text-muted-foreground">Total POs</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-yellow-50 flex items-center justify-center">
                <Truck size={20} className="text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{orders.filter(o => o.status === 'partial').length}</div>
                <div className="text-sm text-muted-foreground">Pending Receipt</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-green-50 flex items-center justify-center">
                <Package size={20} className="text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{grns.length}</div>
                <div className="text-sm text-muted-foreground">Total GRNs</div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-purple-50 flex items-center justify-center">
                <FileText size={20} className="text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{suppliers.length}</div>
                <div className="text-sm text-muted-foreground">Suppliers</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="orders">Purchase Orders</TabsTrigger>
          <TabsTrigger value="pi" data-testid="pi-tab-trigger">Proforma Invoices</TabsTrigger>
          <TabsTrigger value="grn">Goods Receipt (GRN)</TabsTrigger>
          <TabsTrigger value="suppliers">Suppliers ({suppliers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input placeholder="Search orders..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent"></div>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <ShoppingCart size={48} className="mb-4 opacity-50" />
                  <p>No purchase orders found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">PO #</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Items</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Total</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">File</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredOrders.map(po => (
                        <tr key={po.id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 font-mono text-sm">{po.po_number}</td>
                          <td className="px-4 py-3">{po.supplier_name}</td>
                          <td className="px-4 py-3">{po.items?.length || 0}</td>
                          <td className="px-4 py-3 font-mono">₹{(po.total || 0).toLocaleString()}</td>
                          <td className="px-4 py-3">
                            {po.file_name ? (
                              <span className="text-xs text-blue-600 flex items-center gap-1">
                                <FileText size={14} />
                                {po.file_name.substring(0, 20)}...
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(po.status)}`}>
                              {po.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              <Select value={po.status} onValueChange={(v) => handleStatusUpdate(po.id, v)}>
                                <SelectTrigger className="w-24 h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="draft">Draft</SelectItem>
                                  <SelectItem value="sent">Sent</SelectItem>
                                  <SelectItem value="partial">Partial</SelectItem>
                                  <SelectItem value="complete">Complete</SelectItem>
                                </SelectContent>
                              </Select>
                              {po.status !== 'complete' && po.items?.length > 0 && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleCreateGRN(po)}
                                  data-testid={`create-grn-${po.id}`}
                                >
                                  <Package size={14} className="mr-1" />GRN
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="grn" className="mt-4">
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <Input placeholder="Search GRNs..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-0">
              {filteredGRNs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                  <Package size={48} className="mb-4 opacity-50" />
                  <p>No GRNs found</p>
                  <p className="text-sm mt-2">Create a GRN from a Purchase Order to receive goods</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-medium">GRN #</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">PO #</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Supplier</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Items</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Received</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredGRNs.map(grn => (
                        <tr key={grn.id} className="hover:bg-muted/30">
                          <td className="px-4 py-3 font-mono text-sm">{grn.grn_number}</td>
                          <td className="px-4 py-3 font-mono text-sm">{grn.po_number}</td>
                          <td className="px-4 py-3">{grn.supplier_name}</td>
                          <td className="px-4 py-3">{grn.total_items}</td>
                          <td className="px-4 py-3">{grn.received_items_count} / {grn.total_items}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(grn.status)}`}>
                              {grn.status}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-2">
                              {grn.status !== 'complete' && (
                                <>
                                  <Dialog>
                                    <DialogTrigger asChild>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => setSelectedGRN(grn)}
                                        data-testid={`receive-items-${grn.id}`}
                                      >
                                        <MapPin size={14} className="mr-1" />Receive
                                      </Button>
                                    </DialogTrigger>
                                    <DialogContent className="max-w-2xl">
                                      <DialogHeader>
                                        <DialogTitle>Receive Items - {grn.grn_number}</DialogTitle>
                                      </DialogHeader>
                                      <div className="space-y-4 max-h-96 overflow-y-auto">
                                        {grn.items?.map((item, idx) => (
                                          <Card key={idx} className="p-4">
                                            <div className="flex justify-between items-center">
                                              <div>
                                                <p className="font-medium">{item.material_name}</p>
                                                <p className="text-sm text-muted-foreground">
                                                  Ordered: {item.ordered_quantity} | Received: {item.received_quantity || 0}
                                                </p>
                                                {item.warehouse_location && (
                                                  <p className="text-xs text-blue-600 mt-1">
                                                    Location: {item.warehouse_location.warehouse} - {item.warehouse_location.rack || 'N/A'}
                                                  </p>
                                                )}
                                              </div>
                                              {(item.received_quantity || 0) < item.ordered_quantity && (
                                                <Button 
                                                  variant="outline" 
                                                  size="sm"
                                                  onClick={() => {
                                                    setReceiveItem(item);
                                                    setReceiveQty(item.ordered_quantity - (item.received_quantity || 0));
                                                    setReceiveDialogOpen(true);
                                                  }}
                                                  data-testid={`receive-item-${item.material_id}`}
                                                >
                                                  Receive
                                                </Button>
                                              )}
                                              {(item.received_quantity || 0) >= item.ordered_quantity && (
                                                <CheckCircle size={20} className="text-green-600" />
                                              )}
                                            </div>
                                          </Card>
                                        ))}
                                      </div>
                                      <Button 
                                        className="w-full btn-accent mt-4"
                                        onClick={() => handleCompleteGRN(grn.id)}
                                      >
                                        Mark GRN Complete
                                      </Button>
                                    </DialogContent>
                                  </Dialog>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers" className="mt-4">
          {suppliers.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">No suppliers yet</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {suppliers.map(s => (
                <Card key={s.id}>
                  <CardContent className="p-4">
                    <h3 className="font-medium">{s.name}</h3>
                    <p className="text-sm text-muted-foreground">{s.email}</p>
                    {s.phone && <p className="text-sm text-muted-foreground">{s.phone}</p>}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="pi" className="mt-4">
          <PITab suppliers={suppliers} />
        </TabsContent>
      </Tabs>

      {/* Receive Item Dialog */}
      <Dialog open={receiveDialogOpen} onOpenChange={setReceiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Receive Item with Location</DialogTitle>
          </DialogHeader>
          {receiveItem && (
            <div className="space-y-4">
              <div className="bg-muted/50 p-3 rounded">
                <p className="font-medium">{receiveItem.material_name}</p>
                <p className="text-sm text-muted-foreground">
                  Pending: {receiveItem.ordered_quantity - (receiveItem.received_quantity || 0)}
                </p>
              </div>
              <div>
                <Label>Quantity to Receive *</Label>
                <Input 
                  type="number" 
                  value={receiveQty} 
                  onChange={(e) => setReceiveQty(parseInt(e.target.value) || 0)}
                  className="mt-1.5"
                  data-testid="receive-quantity"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Warehouse</Label>
                  <Input 
                    value={warehouseLocation.warehouse} 
                    onChange={(e) => setWarehouseLocation({...warehouseLocation, warehouse: e.target.value})}
                    className="mt-1.5"
                    data-testid="warehouse-name"
                  />
                </div>
                <div>
                  <Label>Zone</Label>
                  <Input 
                    value={warehouseLocation.zone} 
                    onChange={(e) => setWarehouseLocation({...warehouseLocation, zone: e.target.value})}
                    className="mt-1.5"
                    placeholder="e.g., A"
                  />
                </div>
                <div>
                  <Label>Aisle</Label>
                  <Input 
                    value={warehouseLocation.aisle} 
                    onChange={(e) => setWarehouseLocation({...warehouseLocation, aisle: e.target.value})}
                    className="mt-1.5"
                    placeholder="e.g., 1"
                  />
                </div>
                <div>
                  <Label>Rack</Label>
                  <Input 
                    value={warehouseLocation.rack} 
                    onChange={(e) => setWarehouseLocation({...warehouseLocation, rack: e.target.value})}
                    className="mt-1.5"
                    placeholder="e.g., R-01"
                    data-testid="rack-location"
                  />
                </div>
                <div>
                  <Label>Shelf</Label>
                  <Input 
                    value={warehouseLocation.shelf} 
                    onChange={(e) => setWarehouseLocation({...warehouseLocation, shelf: e.target.value})}
                    className="mt-1.5"
                    placeholder="e.g., S-2"
                  />
                </div>
                <div>
                  <Label>Bin</Label>
                  <Input 
                    value={warehouseLocation.bin} 
                    onChange={(e) => setWarehouseLocation({...warehouseLocation, bin: e.target.value})}
                    className="mt-1.5"
                    placeholder="e.g., B-03"
                  />
                </div>
              </div>
              <Button 
                className="w-full btn-accent" 
                onClick={handleReceiveItem}
                data-testid="confirm-receive-btn"
              >
                Confirm Receipt
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProcurementPage;
